import { ChangeKeyConfirmationPopover } from "components/Form/components/SchemaForm/components/ChangeKeyConfirmationPopover";
import { LabelledFormElement } from "components/Form/components/SchemaForm/LabelledFormElement";
import type { ObjectPath } from "components/Form/components/SchemaForm/SchemaForm.types";
import {
    getDefaultRequiredValue,
    getUpdatedData,
} from "components/Form/components/SchemaForm/SchemaFormService";
import { Text } from "components/Form/components/Text/Text";
import { isEqual } from "lodash";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";

export interface ValidatePropertyKeyProps {
    propertyKey: string;
    setError: Dispatch<SetStateAction<string | undefined>>;
}

interface PropertyKeyFieldProps {
    elementData: any;
    currentKey: string;
    formData: Object;
    dataPath: ObjectPath;
    setData: (data: any) => void;
    findSchema: (value: ValidatePropertyKeyProps) => any;
    elementSchema: any;
    placeholder: any;
    currentValue: any;
    currentSchema: any;
    formSchema: object;
    getAdditionalPropertyLabel: (big?: boolean) => string;
}

export const PropertyKeyField = ({
    elementData,
    currentKey,
    setData,
    formData,
    dataPath,
    findSchema,
    elementSchema,
    placeholder,
    currentValue,
    currentSchema,
    formSchema,
    getAdditionalPropertyLabel,
}: PropertyKeyFieldProps) => {
    const [keyError, setKeyError] = useState<string | undefined>(undefined);
    const [isConfirmationPopoverOpen, setConfirmationPopoverOpen] = useState(false);
    const setValueRef = useRef(() => {});

    const fieldRef = useRef<HTMLDivElement>(null);
    const textRef = useRef<HTMLInputElement>() as React.MutableRefObject<HTMLInputElement>;

    useEffect(() => {
        const form =
            fieldRef.current?.closest("#recordPromptForm") || fieldRef.current?.closest("form");
        if (form) {
            form.setAttribute("data-disabled", keyError ? "true" : "false");
        }
    }, [keyError]);

    const setValue = (newKey, foundSchema, currentValue?: undefined) => {
        const newDataEntries = Object.entries(elementData).map(([objectKey, objectValue]) => {
            const newValue = getDefaultRequiredValue(formSchema, foundSchema, null);
            return objectKey === currentKey
                ? [newKey, currentValue ?? newValue]
                : [objectKey, objectValue];
        });
        const newData = Object.fromEntries(newDataEntries);

        setData(getUpdatedData(formData, dataPath, newData));
        setKeyError(undefined);
    };

    return (
        <>
            <div ref={fieldRef}>
                <LabelledFormElement
                    label={getAdditionalPropertyLabel(true)}
                    errorMessage={keyError}
                >
                    <Text
                        inputProps={{
                            onBlur: (event: any) => {
                                const { value } = event.target;
                                if (value === currentKey) {
                                    return;
                                }

                                const foundSchema = findSchema({
                                    propertyKey: value,
                                    setError: setKeyError,
                                });
                                if (foundSchema) {
                                    const defaultValue = getDefaultRequiredValue(
                                        formSchema,
                                        currentSchema,
                                        null
                                    );

                                    if (foundSchema.element === currentSchema.element) {
                                        setValue(value, foundSchema, currentValue);
                                    } else if (isEqual(defaultValue, currentValue)) {
                                        setValue(value, foundSchema);
                                    } else {
                                        setConfirmationPopoverOpen(true);
                                        setValueRef.current = () => setValue(value, foundSchema);
                                    }
                                }
                            },
                            defaultValue: currentKey,
                        }}
                        name={`schemaForm.${elementSchema.label}.${currentKey}`}
                        attributes={{
                            placeholder,
                        }}
                        reference={textRef}
                    />
                </LabelledFormElement>
            </div>
            <ChangeKeyConfirmationPopover
                isOpen={isConfirmationPopoverOpen}
                onClose={() => {
                    setConfirmationPopoverOpen(false);
                    textRef.current.value = currentKey;
                }}
                onSubmit={() => {
                    setConfirmationPopoverOpen(false);
                    setValueRef.current();
                }}
                label={getAdditionalPropertyLabel()}
                currentKey={currentKey}
            />
        </>
    );
};
